import type { DetailsDrawerState } from '@src/components/OMS/Details/DetailsDrawerSlice';
import { OMSView } from '@src/components/OMS/OMSView';
import { useAppStateDispatch, useAppStateSelector } from '@src/providers/AppStateProvider/AppStateProviderHooks';
import { useCallback, useRef } from 'react';
import { openDetails } from '../OMS/Details/DetailsDrawerSlice';
import { closeView, openView, selectView } from '../OMS/OMSSlice';

/**
 *
 * @returns
 */
export function useDetailsDrawer() {
  const view = useAppStateSelector(selectView);
  const state = useAppStateSelector(state => state.detailsDrawer);
  const dispatch = useAppStateDispatch();

  // Use a ref so openDetailsDrawer captures the latest view without needing view in its deps.
  // Including view in deps would cause openDetailsDrawer to change identity on every view change,
  // re-triggering callers' effects (e.g. MobileOrderDetailsRoute) and re-opening the drawer.
  const viewRef = useRef(view);
  viewRef.current = view;

  const openDetailsDrawer = useCallback(
    ({
      entity,
      getDetailsHref,
      onClickTransaction,
    }: {
      entity: DetailsDrawerState['entity'];
      getDetailsHref?: DetailsDrawerState['getDetailsHref'];
      onClickTransaction?: DetailsDrawerState['onClickTransaction'];
    }) => {
      // Don't save DetailsDrawer itself as previousView — goBack would dispatch openView(DetailsDrawer),
      // a no-op that leaves the user stuck on the same view.
      const previousView = viewRef.current !== OMSView.DetailsDrawer ? (viewRef.current ?? undefined) : undefined;
      dispatch(openDetails({ entity, getDetailsHref, onClickTransaction, previousView }));
      dispatch(openView(OMSView.DetailsDrawer));
    },
    [dispatch]
  );

  const closeForm = useCallback(() => {
    dispatch(closeView());
  }, [dispatch]);

  const goBack = useCallback(() => {
    if (state.previousView != null) {
      dispatch(openView(state.previousView));
    } else {
      dispatch(closeView());
    }
  }, [dispatch, state.previousView]);

  return {
    openDetailsDrawer,
    closeDetailsDrawer: closeForm,
    goBack,
    hasPreviousView: state.previousView != null,
    isOpen: view === OMSView.DetailsDrawer,
    entity: state.entity,
    width: state.width,
    getDetailsHref: state.getDetailsHref,
    onClickTransaction: state.onClickTransaction,
  };
}

export type UseDetailsDrawerReturn = ReturnType<typeof useDetailsDrawer>;
